import { ArdoqId } from '@ardoq/api-types';
import type { SortDescriptor } from '@ardoq/table';
import { MetamodelEntityId } from 'organizationMetamodel/workspaces/metamodel/types';
import {
  MetamodelGraphElement,
  metamodelGraphElementFromString,
  metamodelGraphElementToString,
} from 'organizationMetamodel/metamodelGraph/diff/metamodelGraphElement';
import { INVENTORY_ID_SEPARATOR } from 'inventory/consts';
import type { InventoryNavigationState } from 'inventory/inventoryNavigationTypes';
import { presentationParamDeclarations } from 'router/presentation/presentationParamDeclarations';

export type ParamHandler<T> = {
  /** How to read this parameter from a string (in the URL for instance) */
  fromString: (value: string) => T;
  /** How to serialize this parameter to a string (for use in the URL for instance) */
  toString: (value: T) => string;
};

/**
 * A set of parameter declarations, keyed by the name the parameter has in the URL.
 *
 * `fromString` returns `unknown` and `toString` takes `never` so that one
 * record can hold handlers for different value types. `ParamHandler<unknown>`
 * would only accept handlers whose value type is `unknown`.
 */
export type ParamDeclarations = Readonly<
  Record<
    string,
    {
      fromString: (value: string) => unknown;
      toString: (value: never) => string;
    }
  >
>;

/** The values a set of declarations parses to, keyed by parameter name. */
export type ParamsIn<Declarations extends ParamDeclarations> = {
  [Param in Extract<keyof Declarations, string>]: ReturnType<
    Declarations[Param]['fromString']
  >;
};

/** Helper function to ensure types when declaring a parameter */
export const param = <T>(handler: ParamHandler<T>): ParamHandler<T> => handler;

export const jsonParam = <T>(): ParamHandler<T | undefined> =>
  param({
    fromString: value => {
      try {
        return JSON.parse(value);
      } catch {
        return undefined;
      }
    },
    toString: value => JSON.stringify(value),
  });

export const uriComponentParam = param({
  fromString: value => decodeURIComponent(value),
  toString: value => encodeURIComponent(value),
});

export const stringParam = param({
  fromString: value => value,
  toString: value => value,
});

export const booleanParam = param<boolean>({
  fromString: value => value === 'true',
  toString: value => String(value),
});

export const sortingParam = param<SortDescriptor>({
  fromString: value => {
    const [column, direction] = value.split(',');

    return {
      column: decodeURIComponent(column),
      direction: direction === 'asc' ? 'asc' : 'desc',
    };
  },
  toString: value => `${encodeURIComponent(value.column)},${value.direction}`,
});

/**
 * Registry of the well known parameters.
 *
 * These parameters are used for instance in the declarative router.
 *
 * Add a key here with the `param` function to register a new parameter.
 * The type can be inferred, or you can provide it explicitly.
 */
const paramRegistry = {
  workspaceId: param({
    fromString: value => value,
    toString: value => value,
  }),

  metamodelEntityId: param<MetamodelEntityId>({
    fromString: value => value,
    toString: value => value,
  }),

  entityId: param<ArdoqId>({
    fromString: value => value,
    toString: value => value,
  }),

  componentId: param<ArdoqId>({
    fromString: value => value,
    toString: value => value,
  }),

  surveyId: param<ArdoqId>({
    fromString: value => value,
    toString: value => value,
  }),

  groupId: param<ArdoqId>({
    fromString: value => value,
    toString: value => value,
  }),

  userId: param<ArdoqId>({
    fromString: value => value,
    toString: value => value,
  }),

  componentTypeId: param<ArdoqId>({
    fromString: value => value,
    toString: value => value,
  }),

  componentTypeName: uriComponentParam,

  usageWorkspaceId: param<ArdoqId>({
    fromString: value => value,
    toString: value => value,
  }),

  variationKey: uriComponentParam,

  metamodelGraphElement: param<MetamodelGraphElement>({
    fromString: metamodelGraphElementFromString,
    toString: metamodelGraphElementToString,
  }),

  tripleId: param({
    fromString: value => value,
    toString: value => value,
  }),

  testParam: param({
    fromString: value => value,
    toString: value => value,
  }),

  testNumParam: param({
    fromString: value => parseInt(value, 10),
    toString: value => `${value}`,
  }),

  testBarParam: param({
    fromString: value => {
      const [x, y, width, height] = value.split(',').map(x => parseInt(x, 10));
      return { x, y, width, height };
    },
    toString: value =>
      [value.x, value.y, value.width, value.height].map(String).join(','),
  }),

  taskId: param({
    fromString: value => value,
    toString: value => value,
  }),

  fieldId: param<ArdoqId>({
    fromString: value => value,
    toString: value => value,
  }),

  integrationId: param({
    fromString: value => value,
    toString: value => value,
  }),

  presentationId: presentationParamDeclarations.presentationId,

  organizationLabel: param({
    fromString: value => value,
    toString: value => value,
  }),

  selectedSlide: presentationParamDeclarations.selectedSlide,

  context: presentationParamDeclarations.context,

  solutionId: uriComponentParam,

  initiativeId: param<ArdoqId>({
    fromString: value => value,
    toString: value => value,
  }),

  futureStateId: param<ArdoqId>({
    fromString: value => value,
    toString: value => value,
  }),

  reportId: param<ArdoqId>({
    fromString: value => value,
    toString: value => value,
  }),

  /** Id of a document at an external provider, e.g. a Lucidchart document */
  externalDocumentId: param({
    fromString: value => value,
    toString: value => value,
  }),

  workspaceIds: param<ArdoqId[]>({
    fromString: value => value.split(INVENTORY_ID_SEPARATOR),
    toString: value => value.join(INVENTORY_ID_SEPARATOR),
  }),

  componentTypeIds: param<string[]>({
    fromString: value => value.split(INVENTORY_ID_SEPARATOR),
    toString: value => value.join(INVENTORY_ID_SEPARATOR),
  }),

  dashboardId: param<ArdoqId>({
    fromString: value => value,
    toString: value => value,
  }),

  /**
   * The parts of the inventory navigation state that have no URL
   * representation, such as selected columns and hierarchy mode. Only ever
   * used as a hidden parameter, which the router stores as-is in the history
   * state rather than serializing.
   */
  inventoryViewState: jsonParam<InventoryNavigationState>(),

  /** Id of the product tour a `/product-tour/:tourId` URL launches. */
  tourId: param({
    fromString: value => value,
    toString: value => value,
  }),

  loadedState: uriComponentParam,
} as const satisfies Record<string, ParamHandler<any>>;

type ParamType<Param extends ParamNames> = ReturnType<
  (typeof paramRegistry)[Param]['fromString']
>;

export type ParamNames = keyof typeof paramRegistry;
export type Params = {
  [Param in ParamNames]: ParamType<Param>;
};

export type RawStringParamName = {
  [Param in ParamNames]: ParamType<Param> extends string ? Param : never;
}[ParamNames];
export type StringParamName = `:${RawStringParamName}`;

export const readParam = <Param extends ParamNames>(
  param: Param,
  value: string
): Params[Param] => {
  const handler = paramRegistry[param] as unknown as ParamHandler<
    Params[Param]
  >;
  if (!handler) {
    throw new Error(`No handler for reading param ${param}`);
  }
  return handler.fromString(value);
};

export const writeParam = <Param extends ParamNames>(
  param: Param,
  value: Params[Param]
): string => {
  const handler = paramRegistry[param] as unknown as ParamHandler<
    Params[Param]
  >;
  if (!handler) {
    throw new Error(`No handler for writing param ${param}`);
  }
  return handler.toString(value);
};
